import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { GameHistoryDetailsServiceService } from 'src/app/gamification/command-center/services/game-history-details-service.service';
import { GameType } from 'src/app/gamification/command-center/models/games.model';
import { HistoricalTeamObj, IHGameStandardize, IHSelectedMetric, StandardizeRewards, StandardizeRewardsFormat } from 'src/app/gamification/command-center/models/games-parameter.model';

@Component({
    selector: 'cv-admin-game-history-specific-details',
    templateUrl: './cv-admin-game-history-specific-details.component.html',
    styleUrl: './cv-admin-game-history-specific-details.component.scss'
})
export class CvAdminGameHistorySpecificDetailsComponent implements OnInit, OnChanges {

    @Input() game = null;
    @Input() endDate: string = null;
    @Input() gameActions = null;
    @Input() participants: any[];
    @Input() teamsInfo: { [key: number]: HistoricalTeamObj } = {};
    @Input() historyGame: IHGameStandardize = null;

    topParticipants = [];
    metric: IHSelectedMetric;
    extraRewardDetails: Map<number, number> = new Map();
    hasBadges: boolean;
    private formattedRewards: StandardizeRewardsFormat[] = [];
    private readonly RING_COUNT = 5;
    private readonly titleSectionLocalization: Partial<Record<GameType, { key: string; defaultText: string }>> = {
        [GameType.MultiplierMadness]: { key: 'Multipliermadness', defaultText: 'Rewards' },
        [GameType.Connect3]: { key: 'Connect3', defaultText: 'Rewards' },
        [GameType.Blackout]: { key: 'Connectall', defaultText: 'Rewards' },
        [GameType.Milestone]: { key: 'Milestone', defaultText: 'Milestones' },
        [GameType.Bullseye]: { key: 'Bullseye', defaultText: 'Rings' },
        [GameType.TopDog]: { key: 'Topdog', defaultText: 'Places' },
        [GameType.TopTeam]: { key: 'Topteam', defaultText: 'Places' }
    };
    private readonly rankGameType: Partial<Record<GameType, string>> = {
        [GameType.Milestone]: 'milestone',
        [GameType.TopDog]: 'topdog',
        [GameType.TopTeam]: 'topdog' //to avoid duplicate icons in different folders 
    };
    private readonly extraInfoGameType: Set<GameType> = new Set([
        GameType.Milestone, GameType.Bullseye
    ]);
    //games with icon and without details
    private readonly gamesWithIcon: Set<GameType> = new Set([
        GameType.TopDog, GameType.TopTeam
    ]);

    constructor(
        private cvLocalizationService: CVLocalizationService,
        private gameHistoryDetailService: GameHistoryDetailsServiceService,
    ) { }

    ngOnInit() {
        this.loadLocalization();
    }

    private requiresMetricLoading(gameType: GameType): boolean {
        // Extract complex OR condition to reduce branching complexity in main method
        // These game types need metric data for reward calculations or threshold formatting
        return gameType === GameType.MultiplierMadness ||
            gameType === GameType.TopDog ||
            gameType === GameType.TopTeam ||
            gameType === GameType.Bullseye;
    }

    private requiresParticipantProcessing(gameType: GameType): boolean {
        // Separated negative conditions to keep original behavior from the second conditional of the ngOnChange
        return gameType !== GameType.MultiplierMadness && gameType !== GameType.Bullseye;
    }

    private processParticipantSorting(participantsData: any[]): void {
        // Set participant data from input changes
        this.participants = participantsData;
        // Sort participants based on metric direction preference
        if (this.metric.MetricDetail.DefaultMetricDirection == 1) {
            this.participants = this.participants.sort((a, b) => b.metricValue - a.metricValue);
        } else {
            this.participants = this.participants.sort((a, b) => a.metricValue - b.metricValue);
        }
        this.getTopParticipants();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.endDate?.currentValue || changes.game?.currentValue) {
            this.game = changes.game?.currentValue || this.game;
            this.endDate = changes.endDate?.currentValue?.split('T')[0] || this.endDate;

            this.hasBadges = (this.game.GameType !== GameType.MultiplierMadness && this.game.GameType !== GameType.Raffle);
            if (this.game?.RuleID && this.endDate != null) {
                this.cleanVariables();
            }
        }

        if (changes.historyGame && changes.historyGame.currentValue) {
            this.formatRewards();
        }

        if (changes.gameActions && changes.gameActions.currentValue != null) {
            this.gameActions = changes.gameActions?.currentValue || this.gameActions;
            this.getExtraDetails();
        }

        if (changes.participants && changes.participants.currentValue != null) {
            if (this.requiresMetricLoading(this.game?.GameType)) {
                this.metric = this.gameHistoryDetailService.getMetricDetails(this.game.RuleID, this.endDate);

                if (this.requiresParticipantProcessing(this.game?.GameType) && this.metric != null) {
                    this.processParticipantSorting(changes.participants.currentValue);
                }
            }
        }

        if (changes.teamsInfo && changes.teamsInfo.currentValue != null) {
            this.teamsInfo = changes.teamsInfo?.currentValue || this.teamsInfo;
        }
    }

    get titleSection() {
        const keyRecord = this.titleSectionLocalization[this.game?.GameType];
        if (!keyRecord) return '';
        return this.getLocalizationString(`${keyRecord.key}_title`, `${keyRecord.defaultText}`);
    }

    get gameRewards(): StandardizeRewardsFormat[] {
        if (this.game?.RuleID && this.historyGame) {
            let gameRewardsList = [...this.formattedRewards];

            if (this.game?.GameType === GameType.Milestone) {
                gameRewardsList = gameRewardsList?.slice().reverse();
            }

            return gameRewardsList.slice(0, 5);
        }
        return [];
    }

    get areDetailsLoaded(): boolean {
        if (!this.endDate) return false;

        if (!this.game || !this.gameRewards) return false;

        switch (this.game?.GameType) {
            case GameType.TopDog:
            case GameType.TopTeam:
                return !(this.topParticipants && this.metric);
            case GameType.MultiplierMadness:
                return !(this.metric);
            case GameType.Bullseye:
            case GameType.Milestone:
                return !(this.extraRewardDetails.size > 0 || this.gameActions?.length == 0);
            default:
                return false;
        }
    }

    get isRankTypeGame() {
        return !!this.rankGameType[this.game?.GameType];
    }

    get hasExtraInfoGameType() {
        return this.extraInfoGameType.has(this.game?.GameType);
    }

    get hasIconGameType() {
        return this.gamesWithIcon.has(this.game?.GameType);
    }

    get isMultiplierMadness() {
        return this.game?.GameType === GameType.MultiplierMadness;
    }

    get isMilestone() {
        return this.game?.GameType === GameType.Milestone;
    }

    get rankIconClass(): string {
        switch (this.game?.GameType) {
            case GameType.Milestone:
                return 'game-history-specific-details__icon-milestone';
            case GameType.Bullseye:
            case GameType.TopDog:
            case GameType.TopTeam:
                return 'game-history-specific-details__icon-team';
            default: return '';
        }
    }

    formatRewards() {
        if (!this.historyGame || !Array.isArray(this.historyGame.rewards)) {
            this.formattedRewards = [];
            return;
        }
        const rewards = [...this.historyGame.rewards];
        this.formattedRewards = rewards.map((reward: StandardizeRewards) => {
            const bullseyeRings = this.game?.GameType === GameType.Bullseye
                ? Array.from({ length: this.RING_COUNT }, (_, i) => ({
                    id: i,
                    size: this.calculateBullseyeRingSize(i),
                }))
                : undefined;

            return {
                ...reward,
                coinText: this.formatCoin(reward?.coins ?? 0),
                badgeText: reward?.badges ? '1' : '0',
                ...(bullseyeRings && { bullseyeRings })
            }

        }) as StandardizeRewardsFormat[];
    }

    getRankIcons() {
        const gameType = this.rankGameType[this.game?.GameType];
        const iconName = `${gameType}-rank`;
        return `../App_Themes/CXone/icons/gamification/${gameType}-ranks/${iconName}.svg#${iconName}`;
    }

    async loadLocalization() {
        await this.cvLocalizationService.GetSection("Games_History_Specific_Details");
    }

    getLocalizationString(key: string, defaultValue: string) {
        return this.cvLocalizationService.getCachedText('Games_History_Specific_Details', key, defaultValue);
    }

    getGameRewardLabel(
        index: number,
        reward: any,
    ): string {
        switch (this.game?.GameType) {
            case GameType.MultiplierMadness:
                if (reward?.name === 'REWARDS_PER_METRICS') {
                    return this.gameHistoryDetailService.getLocalizedMultiplierMadnessText(this.metric, true, this.getLocalizationString.bind(this));
                } else if (reward?.name === 'MAX_PAYOUT_POSSIBLE') {
                    return this.gameHistoryDetailService.getLocalizedMultiplierMadnessText(this.metric, false, this.getLocalizationString.bind(this));
                }
                return '';
            case GameType.TopDog:
            case GameType.TopTeam:
                return this.gameHistoryDetailService.getLocalizedTierText(index + 1, this.metric, this.topParticipants, this.participants, this.getLocalizationString.bind(this));
            case GameType.Bullseye:
                return this.gameHistoryDetailService.getRangeText(reward, this.getLocalizationString.bind(this), true);
            case GameType.Milestone:
                return this.gameHistoryDetailService.getRangeText(reward, this.getLocalizationString.bind(this));
            case GameType.Connect3:
                return this.gameHistoryDetailService.getConnectGamesText(true, this.getLocalizationString.bind(this));
            case GameType.Blackout:
                return this.gameHistoryDetailService.getConnectGamesText(false, this.getLocalizationString.bind(this));
            case GameType.Raffle:
                return this.gameHistoryDetailService.getRafflePrizeText(index, this.getLocalizationString.bind(this));
            case GameType.Brackets:
                return this.gameHistoryDetailService.getBracketRoundName(index, this.getLocalizationString.bind(this));
            default:
                return '';
        }
    }

    getRewardExtraInfo(
        levelId: string
    ) {
        return this.gameHistoryDetailService.getExtraDetailsText(levelId, this.game?.GameType, this.extraRewardDetails, this.getLocalizationString.bind(this));
    }

    cleanVariables() {
        this.metric = undefined;
        this.extraRewardDetails = new Map();
        this.topParticipants = [];
    }

    getExtraDetails() {
        this.extraRewardDetails = this.gameActions.reduce((map, action) => {
            const key = `${action.ActionLevelID}-${action.LoginID}`;
            if (!map.seen.has(key)) {
                map.seen.add(key);
                map.count.set(action.ActionLevelID, (map.count.get(action.ActionLevelID) || 0) + 1);
            }
            return map;
        }, { seen: new Set<string>(), count: new Map<number, number>() }).count;
    }

    formatCoin(coin: number): string {
        return coin.toLocaleString(CVCultureName);
    }

    getTopParticipants() {
        const { topParticipants, participants } = this.gameHistoryDetailService.getTopParticipants(this.metric, this.game?.GameType, this.participants, this.teamsInfo);
        this.topParticipants = topParticipants
        this.participants = participants;
    }

    getRingStyle(index: number, activeIndex: number): { [key: string]: string } {
        const ringToPaint = this.RING_COUNT - 1 - activeIndex;
        if (index === ringToPaint) {
            return {
                'background': '#008940',
            };
        }
        return { 'background': '#ECF3F8' }
    }

    calculateBullseyeRingSize(index: number): number {
        const diameter = 24;
        const ringThickness = diameter / (this.RING_COUNT * 2);
        return diameter - index * ringThickness * 2;
    }
}
